import { __assign } from "tslib";
import { Logger } from "../Misc/logger";
import { Vector3 } from "../Maths/math.vector";
import { SphereBuilder } from "../Meshes/Builders/sphereBuilder";
import { CylinderBuilder } from "../Meshes/Builders/cylinderBuilder";
import { Ray } from "../Culling/ray";
/**
 * A helper for physics simulations
 * @see https://doc.babylonjs.com/how_to/using_the_physics_engine#further-functionality-of-the-impostor-class
 */
var PhysicsHelper = /** @class */ (function () {
    /**
     * Initializes the Physics helper
     * @param scene Babylon.js scene
     */
    function PhysicsHelper(scene) {
        this._scene = scene;
        this._physicsEngine = this._scene.getPhysicsEngine();
        if (!this._physicsEngine) {
            Logger.Warn('Physics engine not enabled. Please enable the physics before you can use the methods.');
            return;
        }
    }
    /**
     * Applies a radial explosion impulse
     * @param origin the origin of the explosion
     * @param radiusOrEventOptions the radius or the options of radial explosion
     * @param strength the explosion strength
     * @param falloff possible options: Constant & Linear. Defaults to Constant
     * @returns A physics radial explosion event, or null
     */
    PhysicsHelper.prototype.applyRadialExplosionImpulse = function (origin, radiusOrEventOptions, strength, falloff) {
        if (!this._physicsEngine) {
            Logger.Warn('Physics engine not enabled. Please enable the physics before you call this method.');
            return null;
        }
        var impostors = this._physicsEngine.getImpostors();
        if (impostors.length === 0) {
            return null;
        }
        if (typeof radiusOrEventOptions === 'number') {
            radiusOrEventOptions = new PhysicsRadialExplosionEventOptions();
            radiusOrEventOptions.radius = radiusOrEventOptions;
            radiusOrEventOptions.strength = strength || radiusOrEventOptions.strength;
            radiusOrEventOptions.falloff = falloff || radiusOrEventOptions.falloff;
        }
        var event = new PhysicsRadialExplosionEvent(this._scene, radiusOrEventOptions);
        var affectedImpostorsWithData = Array();
        impostors.forEach(function (impostor) {
            var impostorHitData = event.getImpostorHitData(impostor, origin);
            if (!impostorHitData) {
                return;
            }
            impostor.applyImpulse(impostorHitData.force, impostorHitData.contactPoint);
            affectedImpostorsWithData.push({
                impostor: impostor,
                hitData: impostorHitData,
            });
        });
        event.triggerAffectedImpostorsCallback(affectedImpostorsWithData);
        event.dispose(false);
        return event;
    };
    /**
     * Applies a radial explosion force
     * @param origin the origin of the explosion
     * @param radiusOrEventOptions the radius or the options of radial explosion
     * @param strength the explosion strength
     * @param falloff possible options: Constant & Linear. Defaults to Constant
     * @returns A physics radial explosion event, or null
     */
    PhysicsHelper.prototype.applyRadialExplosionForce = function (origin, radiusOrEventOptions, strength, falloff) {
        if (!this._physicsEngine) {
            Logger.Warn('Physics engine not enabled. Please enable the physics before you call the PhysicsHelper.');
            return null;
        }
        var impostors = this._physicsEngine.getImpostors();
        if (impostors.length === 0) {
            return null;
        }
        if (typeof radiusOrEventOptions === 'number') {
            radiusOrEventOptions = new PhysicsRadialExplosionEventOptions();
            radiusOrEventOptions.radius = radiusOrEventOptions;
            radiusOrEventOptions.strength = strength || radiusOrEventOptions.strength;
            radiusOrEventOptions.falloff = falloff || radiusOrEventOptions.falloff;
        }
        var event = new PhysicsRadialExplosionEvent(this._scene, radiusOrEventOptions);
        var affectedImpostorsWithData = Array();
        impostors.forEach(function (impostor) {
            var impostorHitData = event.getImpostorHitData(impostor, origin);
            if (!impostorHitData) {
                return;
            }
            impostor.applyForce(impostorHitData.force, impostorHitData.contactPoint);
            affectedImpostorsWithData.push({
                impostor: impostor,
                hitData: impostorHitData,
            });
        });
        event.triggerAffectedImpostorsCallback(affectedImpostorsWithData);
        event.dispose(false);
        return event;
    };
    /**
     * Creates a gravitational field
     * @param origin the origin of the explosion
     * @param radiusOrEventOptions the radius or the options of radial explosion
     * @param strength the explosion strength
     * @param falloff possible options: Constant & Linear. Defaults to Constant
     * @returns A physics gravitational field event, or null
     */
    PhysicsHelper.prototype.gravitationalField = function (origin, radiusOrEventOptions, strength, falloff) {
        if (!this._physicsEngine) {
            Logger.Warn('Physics engine not enabled. Please enable the physics before you call the PhysicsHelper.');
            return null;
        }
        var impostors = this._physicsEngine.getImpostors();
        if (impostors.length === 0) {
            return null;
        }
        if (typeof radiusOrEventOptions === 'number') {
            radiusOrEventOptions = new PhysicsRadialExplosionEventOptions();
            radiusOrEventOptions.radius = radiusOrEventOptions;
            radiusOrEventOptions.strength = strength || radiusOrEventOptions.strength;
            radiusOrEventOptions.falloff = falloff || radiusOrEventOptions.falloff;
        }
        var event = new PhysicsGravitationalFieldEvent(this, this._scene, origin, radiusOrEventOptions);
        event.dispose(false);
        return event;
    };
    /**
     * Creates a physics updraft event
     * @param origin the origin of the updraft
     * @param radiusOrEventOptions the radius or the options of the updraft
     * @param strength the strength of the updraft
     * @param height the height of the updraft
     * @param updraftMode possible options: Center & Perpendicular. Defaults to Center
     * @returns A physics updraft event, or null
     */
    PhysicsHelper.prototype.updraft = function (origin, radiusOrEventOptions, strength, height, updraftMode) {
        if (!this._physicsEngine) {
            Logger.Warn('Physics engine not enabled. Please enable the physics before you call the PhysicsHelper.');
            return null;
        }
        if (this._physicsEngine.getImpostors().length === 0) {
            return null;
        }
        if (typeof radiusOrEventOptions === 'number') {
            radiusOrEventOptions = new PhysicsUpdraftEventOptions();
            radiusOrEventOptions.radius = radiusOrEventOptions;
            radiusOrEventOptions.strength = strength || radiusOrEventOptions.strength;
            radiusOrEventOptions.height = height || radiusOrEventOptions.height;
            radiusOrEventOptions.updraftMode = updraftMode || radiusOrEventOptions.updraftMode;
        }
        var event = new PhysicsUpdraftEvent(this._scene, origin, radiusOrEventOptions);
        event.dispose(false);
        return event;
    };
    /**
     * Creates a physics vortex event
     * @param origin the of the vortex
     * @param radiusOrEventOptions the radius or the options of the vortex
     * @param strength the strength of the vortex
     * @param height   the height of the vortex
     * @returns a Physics vortex event, or null
     * A physics vortex event or null
     */
    PhysicsHelper.prototype.vortex = function (origin, radiusOrEventOptions, strength, height) {
        if (!this._physicsEngine) {
            Logger.Warn('Physics engine not enabled. Please enable the physics before you call the PhysicsHelper.');
            return null;
        }
        if (this._physicsEngine.getImpostors().length === 0) {
            return null;
        }
        if (typeof radiusOrEventOptions === 'number') {
            radiusOrEventOptions = new PhysicsVortexEventOptions();
            radiusOrEventOptions.radius = radiusOrEventOptions;
            radiusOrEventOptions.strength = strength || radiusOrEventOptions.strength;
            radiusOrEventOptions.height = height || radiusOrEventOptions.height;
        }
        var event = new PhysicsVortexEvent(this._scene, origin, radiusOrEventOptions);
        event.dispose(false);
        return event;
    };
    return PhysicsHelper;
}());
export { PhysicsHelper };
/**
 * Represents a physics radial explosion event
 */
var PhysicsRadialExplosionEvent = /** @class */ (function () {
    /**
     * Initializes a radial explosioin event
     * @param _scene BabylonJS scene
     * @param _options The options for the vortex event
     */
    function PhysicsRadialExplosionEvent(_scene, _options) {
        this._scene = _scene;
        this._options = _options;
        this._dataFetched = false; // check if the data has been fetched. If not, do cleanup
        this._options = __assign(__assign({}, (new PhysicsRadialExplosionEventOptions())), this._options);
    }
    /**
     * Returns the data related to the radial explosion event (sphere).
     * @returns The radial explosion event data
     */
    PhysicsRadialExplosionEvent.prototype.getData = function () {
        this._dataFetched = true;
        return {
            sphere: this._sphere,
        };
    };
    /**
     * Returns the force and contact point of the impostor or false, if the impostor is not affected by the force/impulse.
     * @param impostor A physics imposter
     * @param origin the origin of the explosion
     * @returns {Nullable<PhysicsHitData>} A physics force and contact point, or null
     */
    PhysicsRadialExplosionEvent.prototype.getImpostorHitData = function (impostor, origin) {
        if (impostor.mass === 0) {
            return null;
        }
        if (!this._intersectsWithSphere(impostor, origin, this._options.radius)) {
            return null;
        }
        if (impostor.object.getClassName() !== 'Mesh' && impostor.object.getClassName() !== 'InstancedMesh') {
            return null;
        }
        var impostorObjectCenter = impostor.getObjectCenter();
        var direction = impostorObjectCenter.subtract(origin);
        var ray = new Ray(origin, direction, this._options.radius);
        var hit = ray.intersectsMesh(impostor.object);
        var contactPoint = hit.pickedPoint;
        if (!contactPoint) {
            return null;
        }
        var distanceFromOrigin = Vector3.Distance(origin, contactPoint);
        if (distanceFromOrigin > this._options.radius) {
            return null;
        }
        var multiplier = this._options.falloff === PhysicsRadialImpulseFalloff.Constant
            ? this._options.strength
            : this._options.strength * (1 - (distanceFromOrigin / this._options.radius));
        var force = direction.multiplyByFloats(multiplier, multiplier, multiplier);
        return { force: force, contactPoint: contactPoint, distanceFromOrigin: distanceFromOrigin };
    };
    /**
     * Triggers affecterd impostors callbacks
     * @param affectedImpostorsWithData defines the list of affected impostors (including associated data)
     */
    PhysicsRadialExplosionEvent.prototype.triggerAffectedImpostorsCallback = function (affectedImpostorsWithData) {
        if (this._options.affectedImpostorsCallback) {
            this._options.affectedImpostorsCallback(affectedImpostorsWithData);
        }
    };
    /**
     * Disposes the sphere.
     * @param force Specifies if the sphere should be disposed by force
     */
    PhysicsRadialExplosionEvent.prototype.dispose = function (force) {
        var _this = this;
        if (force === void 0) { force = true; }
        if (force) {
            this._sphere.dispose();
        }
        else {
            setTimeout(function () {
                if (!_this._dataFetched) {
                    _this._sphere.dispose();
                }
            }, 0);
        }
    };
    /*** Helpers ***/
    PhysicsRadialExplosionEvent.prototype._prepareSphere = function () {
        if (!this._sphere) {
            this._sphere = SphereBuilder.CreateSphere("radialExplosionEventSphere", this._options.sphere, this._scene);
            this._sphere.isVisible = false;
        }
    };
    PhysicsRadialExplosionEvent.prototype._intersectsWithSphere = function (impostor, origin, radius) {
        var impostorObject = impostor.object;
        this._prepareSphere();
        this._sphere.position = origin;
        this._sphere.scaling = new Vector3(radius * 2, radius * 2, radius * 2);
        this._sphere._updateBoundingInfo();
        this._sphere.computeWorldMatrix(true);
        return this._sphere.intersectsMesh(impostorObject, true);
    };
    return PhysicsRadialExplosionEvent;
}());
/**
 * Represents a gravitational field event
 */
var PhysicsGravitationalFieldEvent = /** @class */ (function () {
    /**
     * Initializes the physics gravitational field event
     * @param _physicsHelper A physics helper
     * @param _scene BabylonJS scene
     * @param _origin The origin position of the gravitational field event
     * @param _options The options for the vortex event
     */
    function PhysicsGravitationalFieldEvent(_physicsHelper, _scene, _origin, _options) {
        this._physicsHelper = _physicsHelper;
        this._scene = _scene;
        this._origin = _origin;
        this._options = _options;
        this._dataFetched = false; // check if the has been fetched the data. If not, do cleanup
        this._options = __assign(__assign({}, (new PhysicsRadialExplosionEventOptions())), this._options);
        this._tickCallback = this._tick.bind(this);
        this._options.strength = this._options.strength * -1;
    }
    /**
     * Returns the data related to the gravitational field event (sphere).
     * @returns A gravitational field event
     */
    PhysicsGravitationalFieldEvent.prototype.getData = function () {
        this._dataFetched = true;
        return {
            sphere: this._sphere,
        };
    };
    /**
     * Enables the gravitational field.
     */
    PhysicsGravitationalFieldEvent.prototype.enable = function () {
        this._tickCallback.call(this);
        this._scene.registerBeforeRender(this._tickCallback);
    };
    /**
     * Disables the gravitational field.
     */
    PhysicsGravitationalFieldEvent.prototype.disable = function () {
        this._scene.unregisterBeforeRender(this._tickCallback);
    };
    /**
     * Disposes the sphere.
     * @param force The force to dispose from the gravitational field event
     */
    PhysicsGravitationalFieldEvent.prototype.dispose = function (force) {
        var _this = this;
        if (force === void 0) { force = true; }
        if (force) {
            this._sphere.dispose();
        }
        else {
            setTimeout(function () {
                if (!_this._dataFetched) {
                    _this._sphere.dispose();
                }
            }, 0);
        }
    };
    PhysicsGravitationalFieldEvent.prototype._tick = function () {
        // Since the params won't change, we fetch the event only once
        if (this._sphere) {
            this._physicsHelper.applyRadialExplosionForce(this._origin, this._options);
        }
        else {
            var radialExplosionEvent = this._physicsHelper.applyRadialExplosionForce(this._origin, this._options);
            if (radialExplosionEvent) {
                this._sphere = radialExplosionEvent.getData().sphere.clone('radialExplosionEventSphereClone');
            }
        }
    };
    return PhysicsGravitationalFieldEvent;
}());
/**
 * Represents a physics updraft event
 */
var PhysicsUpdraftEvent = /** @class */ (function () {
    /**
     * Initializes the physics updraft event
     * @param _scene BabylonJS scene
     * @param _origin The origin position of the updraft
     * @param _options The options for the updraft event
     */
    function PhysicsUpdraftEvent(_scene, _origin, _options) {
        this._scene = _scene;
        this._origin = _origin;
        this._options = _options;
        this._originTop = Vector3.Zero(); // the most upper part of the cylinder
        this._originDirection = Vector3.Zero(); // used if the updraftMode is perpendicular
        this._cylinderPosition = Vector3.Zero(); // to keep the cylinders position, because normally the origin is in the center and not on the bottom
        this._dataFetched = false; // check if the has been fetched the data. If not, do cleanup
        this._physicsEngine = this._scene.getPhysicsEngine();
        this._options = __assign(__assign({}, (new PhysicsUpdraftEventOptions())), this._options);
        this._origin.addToRef(new Vector3(0, this._options.height / 2, 0), this._cylinderPosition);
        this._origin.addToRef(new Vector3(0, this._options.height, 0), this._originTop);
        if (this._options.updraftMode === PhysicsUpdraftMode.Perpendicular) {
            this._originDirection = this._origin.subtract(this._originTop).normalize();
        }
        this._tickCallback = this._tick.bind(this);
        this._prepareCylinder();
    }
    /**
     * Returns the data related to the updraft event (cylinder).
     * @returns A physics updraft event
     */
    PhysicsUpdraftEvent.prototype.getData = function () {
        this._dataFetched = true;
        return {
            cylinder: this._cylinder,
        };
    };
    /**
     * Enables the updraft.
     */
    PhysicsUpdraftEvent.prototype.enable = function () {
        this._tickCallback.call(this);
        this._scene.registerBeforeRender(this._tickCallback);
    };
    /**
     * Disables the updraft.
     */
    PhysicsUpdraftEvent.prototype.disable = function () {
        this._scene.unregisterBeforeRender(this._tickCallback);
    };
    /**
     * Disposes the cylinder.
     * @param force Specifies if the updraft should be disposed by force
     */
    PhysicsUpdraftEvent.prototype.dispose = function (force) {
        var _this = this;
        if (force === void 0) { force = true; }
        if (!this._cylinder) {
            return;
        }
        if (force) {
            this._cylinder.dispose();
        }
        else {
            setTimeout(function () {
                if (!_this._dataFetched) {
                    _this._cylinder.dispose();
                }
            }, 0);
        }
    };
    PhysicsUpdraftEvent.prototype.getImpostorHitData = function (impostor) {
        if (impostor.mass === 0) {
            return null;
        }
        if (!this._intersectsWithCylinder(impostor)) {
            return null;
        }
        var impostorObjectCenter = impostor.getObjectCenter();
        if (this._options.updraftMode === PhysicsUpdraftMode.Perpendicular) {
            var direction = this._originDirection;
        }
        else {
            var direction = impostorObjectCenter.subtract(this._originTop);
        }
        var distanceFromOrigin = Vector3.Distance(this._origin, impostorObjectCenter);
        var multiplier = this._options.strength * -1;
        var force = direction.multiplyByFloats(multiplier, multiplier, multiplier);
        return { force: force, contactPoint: impostorObjectCenter, distanceFromOrigin: distanceFromOrigin };
    };
    PhysicsUpdraftEvent.prototype._tick = function () {
        var _this = this;
        this._physicsEngine.getImpostors().forEach(function (impostor) {
            var impostorHitData = _this.getImpostorHitData(impostor);
            if (!impostorHitData) {
                return;
            }
            impostor.applyForce(impostorHitData.force, impostorHitData.contactPoint);
        });
    };
    /*** Helpers ***/
    PhysicsUpdraftEvent.prototype._prepareCylinder = function () {
        if (!this._cylinder) {
            this._cylinder = CylinderBuilder.CreateCylinder("updraftEventCylinder", {
                height: this._options.height,
                diameter: this._options.radius * 2,
            }, this._scene);
            this._cylinder.isVisible = false;
        }
    };
    PhysicsUpdraftEvent.prototype._intersectsWithCylinder = function (impostor) {
        var impostorObject = impostor.object;
        this._cylinder.position = this._cylinderPosition;
        return this._cylinder.intersectsMesh(impostorObject, true);
    };
    return PhysicsUpdraftEvent;
}());
/**
 * Represents a physics vortex event
 */
var PhysicsVortexEvent = /** @class */ (function () {
    /**
     * Initializes the physics vortex event
     * @param _scene The BabylonJS scene
     * @param _origin The origin position of the vortex
     * @param _options The options for the vortex event
     */
    function PhysicsVortexEvent(_scene, _origin, _options) {
        this._scene = _scene;
        this._origin = _origin;
        this._options = _options;
        this._originTop = Vector3.Zero(); // the most upper part of the cylinder
        this._cylinderPosition = Vector3.Zero(); // to keep the cylinders position, because normally the origin is in the center and not on the bottom
        this._dataFetched = false; // check if the has been fetched the data. If not, do cleanup
        this._physicsEngine = this._scene.getPhysicsEngine();
        this._options = __assign(__assign({}, (new PhysicsVortexEventOptions())), this._options);
        this._origin.addToRef(new Vector3(0, this._options.height / 2, 0), this._cylinderPosition);
        this._origin.addToRef(new Vector3(0, this._options.height, 0), this._originTop);
        this._tickCallback = this._tick.bind(this);
        this._prepareCylinder();
    }
    /**
     * Returns the data related to the vortex event (cylinder).
     * @returns The physics vortex event data
     */
    PhysicsVortexEvent.prototype.getData = function () {
        this._dataFetched = true;
        return {
            cylinder: this._cylinder,
        };
    };
    /**
     * Enables the vortex.
     */
    PhysicsVortexEvent.prototype.enable = function () {
        this._tickCallback.call(this);
        this._scene.registerBeforeRender(this._tickCallback);
    };
    /**
     * Disables the cortex.
     */
    PhysicsVortexEvent.prototype.disable = function () {
        this._scene.unregisterBeforeRender(this._tickCallback);
    };
    /**
     * Disposes the sphere.
     * @param force
     */
    PhysicsVortexEvent.prototype.dispose = function (force) {
        var _this = this;
        if (force === void 0) { force = true; }
        if (force) {
            this._cylinder.dispose();
        }
        else {
            setTimeout(function () {
                if (!_this._dataFetched) {
                    _this._cylinder.dispose();
                }
            }, 0);
        }
    };
    PhysicsVortexEvent.prototype.getImpostorHitData = function (impostor) {
        if (impostor.mass === 0) {
            return null;
        }
        if (!this._intersectsWithCylinder(impostor)) {
            return null;
        }
        if (impostor.object.getClassName() !== 'Mesh' && impostor.object.getClassName() !== 'InstancedMesh') {
            return null;
        }
        var impostorObjectCenter = impostor.getObjectCenter();
        var originOnPlane = new Vector3(this._origin.x, impostorObjectCenter.y, this._origin.z); // the distance to the origin as if both objects were on a plane (Y-axis)
        var originToImpostorDirection = impostorObjectCenter.subtract(originOnPlane);
        var ray = new Ray(originOnPlane, originToImpostorDirection, this._options.radius);
        var hit = ray.intersectsMesh(impostor.object);
        var contactPoint = hit.pickedPoint;
        if (!contactPoint) {
            return null;
        }
        var absoluteDistanceFromOrigin = hit.distance / this._options.radius;
        var directionToOrigin = contactPoint.normalize();
        if (absoluteDistanceFromOrigin > this._options.centripetalForceThreshold) {
            directionToOrigin = directionToOrigin.negate();
        }
        if (absoluteDistanceFromOrigin > this._options.centripetalForceThreshold) {
            var forceX = directionToOrigin.x * this._options.centripetalForceMultiplier;
            var forceY = directionToOrigin.y * this._options.updraftForceMultiplier;
            var forceZ = directionToOrigin.z * this._options.centripetalForceMultiplier;
        }
        else {
            var perpendicularDirection = Vector3.Cross(originOnPlane, impostorObjectCenter).normalize();
            var forceX = (perpendicularDirection.x + directionToOrigin.x) * this._options.centrifugalForceMultiplier;
            var forceY = this._originTop.y * this._options.updraftForceMultiplier;
            var forceZ = (perpendicularDirection.z + directionToOrigin.z) * this._options.centrifugalForceMultiplier;
        }
        var force = new Vector3(forceX, forceY, forceZ);
        force = force.multiplyByFloats(this._options.strength, this._options.strength, this._options.strength);
        return { force: force, contactPoint: impostorObjectCenter, distanceFromOrigin: absoluteDistanceFromOrigin };
    };
    PhysicsVortexEvent.prototype._tick = function () {
        var _this = this;
        this._physicsEngine.getImpostors().forEach(function (impostor) {
            var impostorHitData = _this.getImpostorHitData(impostor);
            if (!impostorHitData) {
                return;
            }
            impostor.applyForce(impostorHitData.force, impostorHitData.contactPoint);
        });
    };
    /*** Helpers ***/
    PhysicsVortexEvent.prototype._prepareCylinder = function () {
        if (!this._cylinder) {
            this._cylinder = CylinderBuilder.CreateCylinder("vortexEventCylinder", {
                height: this._options.height,
                diameter: this._options.radius * 2,
            }, this._scene);
            this._cylinder.isVisible = false;
        }
    };
    PhysicsVortexEvent.prototype._intersectsWithCylinder = function (impostor) {
        var impostorObject = impostor.object;
        this._cylinder.position = this._cylinderPosition;
        return this._cylinder.intersectsMesh(impostorObject, true);
    };
    return PhysicsVortexEvent;
}());
/**
 * Options fot the radial explosion event
 * @see https://doc.babylonjs.com/how_to/using_the_physics_engine#further-functionality-of-the-impostor-class
 */
var PhysicsRadialExplosionEventOptions = /** @class */ (function () {
    function PhysicsRadialExplosionEventOptions() {
        /**
         * The radius of the sphere for the radial explosion.
         */
        this.radius = 5;
        /**
         * The strenth of the explosion.
         */
        this.strength = 10;
        /**
         * The strenght of the force in correspondence to the distance of the affected object
         */
        this.falloff = PhysicsRadialImpulseFalloff.Constant;
        /**
         * Sphere options for the radial explosion.
         */
        this.sphere = { segments: 32, diameter: 1 };
    }
    return PhysicsRadialExplosionEventOptions;
}());
export { PhysicsRadialExplosionEventOptions };
/**
 * Options fot the updraft event
 * @see https://doc.babylonjs.com/how_to/using_the_physics_engine#further-functionality-of-the-impostor-class
 */
var PhysicsUpdraftEventOptions = /** @class */ (function () {
    function PhysicsUpdraftEventOptions() {
        /**
         * The radius of the cylinder for the vortex
         */
        this.radius = 5;
        /**
         * The strenth of the updraft.
         */
        this.strength = 10;
        /**
         * The height of the cylinder for the updraft.
         */
        this.height = 10;
        /**
         * The mode for the the updraft.
         */
        this.updraftMode = PhysicsUpdraftMode.Center;
    }
    return PhysicsUpdraftEventOptions;
}());
export { PhysicsUpdraftEventOptions };
/**
 * Options fot the vortex event
 * @see https://doc.babylonjs.com/how_to/using_the_physics_engine#further-functionality-of-the-impostor-class
 */
var PhysicsVortexEventOptions = /** @class */ (function () {
    function PhysicsVortexEventOptions() {
        /**
         * The radius of the cylinder for the vortex
         */
        this.radius = 5;
        /**
         * The strenth of the vortex.
         */
        this.strength = 10;
        /**
         * The height of the cylinder for the vortex.
         */
        this.height = 10;
        /**
         * At which distance, relative to the radius the centripetal forces should kick in? Range: 0-1
         */
        this.centripetalForceThreshold = 0.7;
        /**
         * This multiplier determines with how much force the objects will be pushed sideways/around the vortex, when below the treshold.
         */
        this.centripetalForceMultiplier = 5;
        /**
         * This multiplier determines with how much force the objects will be pushed sideways/around the vortex, when above the treshold.
         */
        this.centrifugalForceMultiplier = 0.5;
        /**
         * This multiplier determines with how much force the objects will be pushed upwards, when in the vortex.
         */
        this.updraftForceMultiplier = 0.02;
    }
    return PhysicsVortexEventOptions;
}());
export { PhysicsVortexEventOptions };
/**
* The strenght of the force in correspondence to the distance of the affected object
* @see https://doc.babylonjs.com/how_to/using_the_physics_engine#further-functionality-of-the-impostor-class
*/
export var PhysicsRadialImpulseFalloff;
(function (PhysicsRadialImpulseFalloff) {
    /** Defines that impulse is constant in strength across it's whole radius */
    PhysicsRadialImpulseFalloff[PhysicsRadialImpulseFalloff["Constant"] = 0] = "Constant";
    /** Defines that impulse gets weaker if it's further from the origin */
    PhysicsRadialImpulseFalloff[PhysicsRadialImpulseFalloff["Linear"] = 1] = "Linear";
})(PhysicsRadialImpulseFalloff || (PhysicsRadialImpulseFalloff = {}));
/**
 * The strength of the force in correspondence to the distance of the affected object
 * @see https://doc.babylonjs.com/how_to/using_the_physics_engine#further-functionality-of-the-impostor-class
 */
export var PhysicsUpdraftMode;
(function (PhysicsUpdraftMode) {
    /** Defines that the upstream forces will pull towards the top center of the cylinder */
    PhysicsUpdraftMode[PhysicsUpdraftMode["Center"] = 0] = "Center";
    /** Defines that once a impostor is inside the cylinder, it will shoot out perpendicular from the ground of the cylinder */
    PhysicsUpdraftMode[PhysicsUpdraftMode["Perpendicular"] = 1] = "Perpendicular";
})(PhysicsUpdraftMode || (PhysicsUpdraftMode = {}));
//# sourceMappingURL=physicsHelper.js.map